import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { api, formatApiErrorDetail } from "../../lib/api";
import { Skeleton } from "../../components/ui/skeleton";
import { Sparkles, MapPin, Clock, Phone, Briefcase, AlertCircle } from "lucide-react";
import { formatISTDayLabel, formatISTTime } from "../../lib/datetime";
import {
  YET_TO_CHECK_IN,
  ON_APPROVED_LEAVE,
  NOT_REPORTED,
  NOT_CAPTURED,
  NO_VISIT_RECORDED,
  lateCheckInCopy,
  joinParts,
  plural,
} from "../../lib/vocabulary";
import { scopeSummary } from "../../lib/population";
import TerritoryLabel from "../../components/common/TerritoryLabel";
import "leaflet/dist/leaflet.css";
import StickyHeading from "../../components/common/StickyHeading";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

const STATUS_CLS = {
  "Checked In": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Not Yet": "bg-amber-50 text-amber-700 border-amber-200",
  "On Leave": "bg-sky-50 text-sky-700 border-sky-200",
};

/**
 * The badge words. A status the app has not been taught is NOT painted as a
 * neutral chip carrying whatever string arrived — that is how an unrecognised
 * value reads as a normal, understood state. It is named as unreported and the
 * raw value is shown underneath, so whoever has to fix it can see it.
 */
const STATUS_LABEL = {
  "Checked In": "Checked in",
  "Not Yet": "Not yet",
  "On Leave": "On leave",
};
const UNKNOWN_STATUS_CLS = "bg-white text-amber-800 border-amber-300 border-dashed";

const BIHAR_CENTER = [25.0961, 85.3131];

/**
 * A PIN MEANS "THIS PERSON CHECKED IN TODAY, HERE" (Pattern A / RC-11).
 *
 * The page filtered on `last_seen_location` alone, which the backend fills
 * from the most recent check-in of ANY day. A rep last seen three weeks ago
 * therefore got a pin on a map titled "Where the team is today" — and, being
 * first in the list, the map CENTRED on that three-week-old point. A stale
 * location is still shown, on the card, muted and carrying its own date; it is
 * never a pin.
 *
 * `day` is the payload's own `date` rather than the browser's idea of today:
 * the rows and the day they describe must come from one source.
 */
export function pinnedMembers(data) {
  const day = data?.date;
  if (!day) return [];
  return (data.members || []).filter(
    (m) =>
      m.status === "Checked In" &&
      m.last_seen_date === day &&
      Number.isFinite(m.last_seen_location?.lat) &&
      Number.isFinite(m.last_seen_location?.lng)
  );
}

/** Members holding a location that is NOT today's check-in — card only. */
export function staleLocationMembers(data) {
  const day = data?.date;
  const pinnedIds = new Set(pinnedMembers(data).map((m) => m.id));
  return (data?.members || []).filter(
    (m) =>
      !pinnedIds.has(m.id) &&
      Number.isFinite(m.last_seen_location?.lat) &&
      Number.isFinite(m.last_seen_location?.lng) &&
      m.last_seen_date !== day
  );
}

/**
 * `10:00 AM` for the Settings hour — through the one clock, so the label
 * cannot name a different zone from the sentence around it. Returns "" when
 * Settings has not answered: `lateCheckInCopy` then says something neutral
 * rather than inventing a policy value (RC-13).
 */
export function alertHourLabel(hour) {
  const h = Number(hour);
  if (hour === null || hour === undefined || !Number.isInteger(h) || h < 0 || h > 23) return "";
  const now = new Date();
  // IST is UTC+5:30, so `h`:00 IST is (h-5):(-30) UTC — Date.UTC normalises it.
  const at = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), h - 5, -30));
  return formatISTTime(at, { label: false });
}

export default function FieldMapPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  // The check-in alert hour, from Settings. No fallback constant: a hardcoded
  // 10 here is the defect this replaces, and it would be wrong precisely when
  // the fetch failed.
  const [alertHour, setAlertHour] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/field-map")
      .then(({ data }) => !cancelled && setData(data))
      .catch((e) => !cancelled && setError(formatApiErrorDetail(e.response?.data?.detail) || e.message))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    api.get("/settings/approval-policy")
      .then(({ data }) => !cancelled && setAlertHour(data?.check_in_alert_hour))
      // A missing threshold costs the sentence its hour, not the page its
      // content — the copy degrades to the neutral wording on its own.
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const pinned = useMemo(() => pinnedMembers(data), [data]);
  const stale = useMemo(() => staleLocationMembers(data), [data]);
  const thresholdLabel = alertHourLabel(alertHour);

  return (
    <div className="space-y-6" data-testid="field-map-page">
      {/* The heading pins; the map below scrolls away like any other content.
          The map must never be sticky itself — at 400px tall it would pin over
          half the viewport and bury the member cards it exists to introduce. */}
      <StickyHeading
        icon={Sparkles}
        label="Field Map"
        title="Where the team is today"
        subtitle={
          data
            // NOT "sales officers" (RC-9). The endpoint scopes by team, which
            // for a Sales Manager includes himself, and company-wide it
            // includes Sales Managers and the Admin Manager. The payload
            // carries no roles, so the only honest noun is the general one.
            ? joinParts([
                scopeSummary(data.total, "people in scope"),
                `${data.checked_in} checked in`,
                `${data.not_yet} yet to check in`,
                `${data.on_leave} on approved leave`,
              ])
            : "Loading…"
        }
        testId="field-map-heading"
      />

      {error && <div className="rounded-md border border-red-200 bg-red-50 text-red-700 text-sm px-3 py-2">{error}</div>}

      {/* THE SHUT GATE, SAID OUT LOUD. Without this the map looks like a map
          that is simply not working: no pins, no explanation, and a manager
          concluding the team is nowhere. It is switched off, and the words for
          that come from the backend that switches it. */}
      {data?.location_capture && !data.location_capture.enabled && (
        <div
          className="rounded-md border border-amber-200 bg-amber-50 text-amber-900 text-sm px-3 py-2"
          data-testid="field-map-capture-off"
        >
          <p className="font-semibold">{data.location_capture.headline}</p>
          <p className="mt-0.5 text-xs">{data.location_capture.detail}</p>
        </div>
      )}

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {[0,1,2,3,4,5].map((i) => <Skeleton key={i} className="h-36 rounded-xl" />)}
        </div>
      ) : data?.members?.length === 0 ? (
        <div className="bg-white border border-callisto rounded-xl p-10 text-center">
          <p className="text-sm font-semibold text-callisto-navy">Nobody is in scope.</p>
          <p className="mt-1 text-xs text-callisto-muted">Invite someone from the Team page to start seeing field activity here.</p>
        </div>
      ) : (
        <>
          {/* `relative z-0` caps Leaflet's internal panes (z-index 400–700)
              inside this wrapper's stacking context; without it they paint
              over the pinned heading (z-20) while the map scrolls past. */}
          <div className="relative z-0 rounded-xl overflow-hidden border border-callisto" style={{ height: "400px" }}>
            <MapContainer
              center={pinned.length > 0 ? [pinned[0].last_seen_location.lat, pinned[0].last_seen_location.lng] : BIHAR_CENTER}
              zoom={pinned.length > 0 ? 11 : 7}
              style={{ height: "100%", width: "100%" }}
            >
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              {pinned.map((m) => (
                <Marker key={m.id} position={[m.last_seen_location.lat, m.last_seen_location.lng]}>
                  <Popup>
                    <strong>{m.name}</strong><br />
                    {m.last_seen_area}<br />
                    Checked in {formatISTTime(m.today_check_in_time)}
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>

          {/* An empty map is otherwise unexplained, and a map with fewer pins
              than people reads as missing data rather than as missing
              check-ins. Say which it is. */}
          <p className="text-[11px] text-callisto-muted" data-testid="field-map-pin-note">
            {joinParts([
              pinned.length > 0
                ? `${plural(pinned.length, "pin")} — one per check-in today.`
                : "No check-in has been captured today, so there is nothing to pin.",
              stale.length > 0
                ? `${plural(stale.length, "person", "people")} last had a location on an earlier day: shown on the card with its date, not on the map.`
                : null,
            ], " ")}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="field-map-grid">
            {data?.members?.map((m) => (
              <FieldMapCard
                key={m.id}
                member={m}
                today={data.date}
                thresholdLabel={thresholdLabel}
                reasonLabels={data.location_capture?.not_captured_reason_labels}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function FieldMapCard({ member, today, thresholdLabel, reasonLabels }) {
  const m = member;
  const known = Object.prototype.hasOwnProperty.call(STATUS_LABEL, m.status);
  const statusCls = known ? STATUS_CLS[m.status] : UNKNOWN_STATUS_CLS;
  const checkedInToday = m.status === "Checked In";
  const locationIsToday = m.last_seen_date === today;

  return (
    <div
      className={`bg-white border rounded-xl p-4 transition-all hover:-translate-y-0.5 hover:shadow-md ${m.alert ? "border-red-300 ring-1 ring-red-200" : "border-callisto"}`}
      data-testid={`field-map-card-${m.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="text-sm font-bold text-callisto-navy truncate">{m.name}</h3>
          {m.territory && (
            <p className="text-[11px] text-callisto-muted mt-0.5 flex items-center gap-1">
              <Briefcase size={11} className="shrink-0" />
              <TerritoryLabel
                value={m.territory}
                unmigrated={m.territory_unmigrated}
                review="icon"
                testId={`field-map-territory-${m.id}`}
              />
            </p>
          )}
        </div>
        <span
          className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 border shrink-0 ${statusCls}`}
          data-testid={`field-map-status-${m.id}`}
        >
          {known ? STATUS_LABEL[m.status] : NOT_REPORTED}
        </span>
      </div>

      <div className="mt-3 space-y-1.5 text-[12px] text-callisto-body">
        {checkedInToday ? (
          <>
            <p className="flex items-center gap-1.5">
              <Clock size={12} className="text-callisto-gold" />
              In {formatISTTime(m.today_check_in_time)}
              {m.today_check_out_time && ` · Out ${formatISTTime(m.today_check_out_time)}`}
            </p>
            {m.last_seen_area && (
              <p className="flex items-center gap-1.5">
                <MapPin size={12} className="text-callisto-gold" />
                {m.last_seen_area}
                {m.last_seen_location && (
                  <span className="text-[10px] text-callisto-muted font-mono">
                    ({m.last_seen_location.lat.toFixed(3)}, {m.last_seen_location.lng.toFixed(3)})
                  </span>
                )}
              </p>
            )}
            {/* A CHECK-IN WITH NO POSITION IS "NOT CAPTURED", AND SAYS WHY.
                Left silent, a card with a time and no coordinate reads as a
                map that lost him — the same inversion Pattern A exists for.
                The reason's words come from the server; a reason this build
                has never been taught is named as one rather than blanked. */}
            {m.today_position_status === "not_captured" && (
              <p
                className="flex items-start gap-1.5 text-callisto-muted"
                data-testid={`field-map-position-${m.id}`}
              >
                <MapPin size={12} className="shrink-0 mt-0.5" />
                <span>
                  {NOT_CAPTURED}{" "}
                  {reasonLabels?.[m.today_position_not_captured_reason] ||
                    "No reason for this was reported."}
                </span>
              </p>
            )}
          </>
        ) : m.status === "On Leave" ? (
          <p className="text-callisto-muted">{ON_APPROVED_LEAVE}</p>
        ) : m.status === "Not Yet" ? (
          <>
            <p className="flex items-center gap-1.5">
              {m.alert ? <AlertCircle size={12} className="text-red-600" /> : <Clock size={12} className="text-callisto-muted" />}
              <span className={m.alert ? "text-red-700 font-semibold" : "text-callisto-muted"}>
                {m.alert ? lateCheckInCopy(thresholdLabel) : YET_TO_CHECK_IN}
              </span>
            </p>
            {m.last_seen_area && (
              // A LOCATION FROM ANOTHER DAY, said as one. It is muted, dated,
              // and told plainly that it is not on the map — the same fact the
              // pin rule enforces, in words.
              <p className="flex items-start gap-1.5 text-callisto-muted italic" data-testid={`field-map-stale-${m.id}`}>
                <MapPin size={12} className="shrink-0 mt-0.5" />
                <span>
                  {joinParts([
                    `Last seen ${m.last_seen_area}`,
                    m.last_seen_date ? formatISTDayLabel(m.last_seen_date) : null,
                  ])}
                  {m.last_seen_location && !locationIsToday ? " — not on today's map" : ""}
                </span>
              </p>
            )}
          </>
        ) : (
          <p className="text-amber-800" data-testid={`field-map-unknown-status-${m.id}`}>
            {NOT_REPORTED} — the server sent {m.status ? `"${m.status}"` : "no status"}, which this
            app does not recognise. Nothing about today is being claimed here.
          </p>
        )}
      </div>

      <p className="flex items-center gap-1.5 mt-1">
        {/* THE VISIT COUNT IS ONLY A COUNT WHEN THERE IS A DAY TO COUNT IN.
            "0 visits today" was rendered unconditionally, so a card reading
            "Yet to check in today" asserted a zero as though it were a result
            (Pattern A). Off a check-in, the chip is suppressed. */}
        {checkedInToday && (m.today_visit_count === 0 || m.today_visit_count > 0) && (
          <span
            className="inline-flex items-center justify-center h-5 px-2 rounded-full bg-callisto-navy text-callisto-gold text-[10px]"
            data-testid={`field-map-visits-${m.id}`}
          >
            {m.today_visit_count === 0 ? NO_VISIT_RECORDED : `${plural(m.today_visit_count, "visit")} today`}
          </span>
        )}
        {m.phone && (
          <a href={`tel:${m.phone}`} className="ml-auto inline-flex items-center gap-1 text-callisto-navy hover:text-callisto-gold text-[11px]">
            <Phone size={11} /> {m.phone}
          </a>
        )}
      </p>
    </div>
  );
}
